import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip'
import {
  Column as TanstackColumn,
  Table as TanstackTable,
} from '@tanstack/react-table'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  AlertTriangle,
  ArrowDown,
  ArrowDownUp,
  ArrowUp,
  Check,
  ChevronDown,
  Plus,
  ShieldCheckIcon,
  X,
} from 'lucide-react'
import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { paths } from 'src/routes/paths'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useProductsClosedApi } from 'src/api/db/apiProductsClosed'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Image,
} from 'src/shared/components/ui'
import { Badge } from 'src/shared/components/ui/badge'
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu'

import { Product } from './consts'
import { TableProduct } from './types'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shared/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'src/shared/components/ui/command'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/shared/components/ui/collapsible'
import { cn } from 'src/shared/components/ui/utils'
import { useCategoryApi } from 'src/api/db/apiCategory'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import type { CategoryChange } from './types'

dayjs.extend(relativeTime)

type CategoryProductItem = {
  id: number
  category: { title: string | null }
  category_id: number | null
}

const SortableHeader = ({
  column,
  children,
}: {
  column: TanstackColumn<Product, unknown>
  children: React.ReactNode
}) => {
  const { t } = useLocalization()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Row
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="cursor-pointer items-center"
        >
          {children}
          {column.getIsSorted() === 'asc' ? (
            <ArrowUp className="ml-2 h-4 w-4" />
          ) : column.getIsSorted() === 'desc' ? (
            <ArrowDown className="ml-2 h-4 w-4" />
          ) : (
            <ArrowDownUp className="ml-2 h-4 w-4" />
          )}
        </Row>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
          {t('Sort Ascending', 'Sortera stigande')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
          {t('Sort Descending', 'Sortera fallande')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const SearchableCategorySelect = ({
  category,
  allCategories,
  productId,
  onDelete,
  onUpdate,
}: {
  category: { id: number; category: { title: string | null } }
  allCategories:
    | {
        data?: Array<{ id: number; title: string | null }> | null
      }
    | undefined
  productId: number
  onDelete?: (categoryProductId: number) => void
  onUpdate?: (categoryProductId: number, newCategoryId: number) => void
}) => {
  const { t } = useLocalization()
  const [open, setOpen] = useState(false)
  const selectedCategory = allCategories?.data?.find(
    (cat) => cat.title === category.category?.title
  )
  const selectedValue = selectedCategory?.title ?? category.category?.title

  return (
    <Row className="gap-1 items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'min-w-[200px] flex w-full items-center justify-between rounded-md bg-white px-2.5 py-1.5 text-xs ring-offset-background focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 border border-gray-200 hover:border-gray-300 transition-colors font-medium text-gray-700 hover:bg-gray-50'
            )}
          >
            <span className="line-clamp-1 text-left">
              {t(
                selectedValue?.split(',')[0] ?? '',
                selectedValue?.split(',')[1] ?? ''
              )}
            </span>
            <ChevronDown className="h-3 w-3 ml-1.5 opacity-60 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={t('Search categories...', 'Sök kategorier...')}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>
                {t('No category found.', 'Ingen kategori hittades.')}
              </CommandEmpty>
              <CommandGroup>
                {allCategories?.data?.map((cat) => (
                  <CommandItem
                    key={cat.id}
                    value={cat?.title ?? ''}
                    onSelect={(value) => {
                      const selectedCat = allCategories?.data?.find(
                        (c) => c.title === value
                      )
                      if (selectedCat && onUpdate) {
                        onUpdate(category.id, selectedCat.id)
                        setOpen(false)
                      }
                    }}
                  >
                    {t(
                      cat.title?.split(',')[0] ?? '',
                      cat.title?.split(',')[1] ?? ''
                    )}
                    <Check
                      className={cn(
                        'ml-auto h-4 w-4',
                        selectedValue === cat.title
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {onDelete && (
        <button
          className="p-1 hover:bg-red-100 rounded transition-colors shrink-0"
          onClick={() => onDelete(category.id)}
          aria-label="Remove category"
        >
          <X className="h-3 w-3 text-red-600" />
        </button>
      )}
    </Row>
  )
}

const AddCategoryButton = ({
  allCategories,
  existingCategoryIds,
  productId,
  onAdd,
}: {
  allCategories:
    | {
        data?: Array<{ id: number; title: string | null }> | null
      }
    | undefined
  existingCategoryIds: number[]
  productId: number
  onAdd?: (categoryId: number) => void
}) => {
  const { t } = useLocalization()
  const [open, setOpen] = useState(false)

  const availableCategories =
    allCategories?.data?.filter(
      (cat) => !existingCategoryIds.includes(cat.id)
    ) ?? []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="p-0 hover:bg-blue-50 rounded-md transition-colors border border-transparent hover:border-blue-200"
          aria-label={t('Add category', 'Lägg till kategori')}
        >
          <Plus className="h-4 w-4 text-blue-600" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t('Search categories...', 'Sök kategorier...')}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>
              {t('No category found.', 'Ingen kategori hittades.')}
            </CommandEmpty>
            <CommandGroup>
              {availableCategories.map((cat) => (
                <CommandItem
                  key={cat.id}
                  value={cat?.title ?? ''}
                  onSelect={() => {
                    onAdd?.(cat.id)
                    setOpen(false)
                  }}
                >
                  {t(
                    cat.title?.split(',')[0] ?? '',
                    cat.title?.split(',')[1] ?? ''
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const CollapsibleCategories = ({
  categories,
  allCategories,
  productId,
  onDelete,
  onUpdate,
  onAdd,
}: {
  categories: CategoryProductItem[]
  allCategories:
    | {
        data?: Array<{ id: number; title: string | null }> | null
      }
    | undefined
  productId: number
  onDelete?: (categoryProductId: number) => void
  onUpdate?: (categoryProductId: number, newCategoryId: number) => void
  onAdd?: (categoryId: number) => void
}) => {
  const { t } = useLocalization()
  const [isOpen, setIsOpen] = useState(false)

  const existingCategoryIds = categories
    .map((c) => c.category_id)
    .filter((id): id is number => id !== null)

  const addButton = (
    <AddCategoryButton
      allCategories={allCategories}
      existingCategoryIds={existingCategoryIds}
      productId={productId}
      onAdd={onAdd}
    />
  )

  if (categories.length === 0) {
    return addButton
  }

  const firstCategory = categories[0]
  const remainingCategories = categories.slice(1)

  if (remainingCategories.length === 0) {
    return (
      <Row className="items-center">
        <SearchableCategorySelect
          category={firstCategory}
          allCategories={allCategories}
          productId={productId}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
        {addButton}
      </Row>
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Column className="gap-1.5">
        <Row className="gap-1.5 items-center">
          <SearchableCategorySelect
            category={firstCategory}
            allCategories={allCategories}
            productId={productId}
            onDelete={onDelete}
            onUpdate={onUpdate}
          />
          {addButton}
        </Row>
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors py-0.5">
            <ChevronDown
              className={cn(
                'h-3 w-3 transition-transform',
                isOpen && 'rotate-180'
              )}
            />
            <span>
              {isOpen
                ? t('Show less', 'Visa mindre')
                : t(
                    `+${remainingCategories.length} more`,
                    `+${remainingCategories.length} till`
                  )}
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column className="gap-1.5">
            {remainingCategories.map((category) => (
              <SearchableCategorySelect
                key={category.id}
                category={category}
                allCategories={allCategories}
                productId={productId}
                onDelete={onDelete}
                onUpdate={onUpdate}
              />
            ))}
          </Column>
        </CollapsibleContent>
      </Column>
    </Collapsible>
  )
}

const PENDING_ADD_ID_OFFSET = 1000000

function makePendingAddId(productId: number, categoryId: number): number {
  return -(productId * PENDING_ADD_ID_OFFSET + categoryId)
}

function isPendingAddId(id: number): boolean {
  return id < 0
}

export const useColumns = ({
  pendingCategoryChanges,
  setPendingCategoryChanges,
  productDetailBasePath,
}: {
  pendingCategoryChanges: CategoryChange[]
  setPendingCategoryChanges: React.Dispatch<
    React.SetStateAction<CategoryChange[]>
  >
  productDetailBasePath?: string
}) => {
  const { t } = useLocalization()
  const navigate = useNavigate()
  const currency = useOrganizationCurrency()
  const { deleteProduct } = useProductsClosedApi({})
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [productToDelete, setProductToDelete] = useState<number | null>(null)
  const {
    allCategories,
    createCategoryProduct,
    updateCategoryProduct,
    deleteCategoryProduct,
  } = useCategoryApi({})

  const addChange = useCallback(
    (change: CategoryChange) => {
      setPendingCategoryChanges((prev) => {
        if (change.type === 'add') {
          return [...prev, change]
        }
        if (change.type === 'update') {
          const filtered = prev.filter(
            (c) =>
              !(
                c.type === 'update' &&
                c.categoryProductId === change.categoryProductId
              )
          )
          return [...filtered, change]
        }
        if (change.type === 'delete') {
          if (isPendingAddId(change.categoryProductId)) {
            const n = -change.categoryProductId
            const categoryId = n % PENDING_ADD_ID_OFFSET
            const productId = Math.floor(n / PENDING_ADD_ID_OFFSET)
            return prev.filter(
              (c) =>
                !(
                  c.type === 'add' &&
                  c.productId === productId &&
                  c.categoryId === categoryId
                )
            )
          }
          return [...prev, change]
        }
        return prev
      })
    },
    [setPendingCategoryChanges]
  )

  const getEffectiveCategories = useCallback(
    (
      productId: number,
      serverCategories: Array<{
        id: number
        category: { title: string | null }
        category_id: number | null
      }>
    ): CategoryProductItem[] => {
      const pendingDeletes = new Set(
        pendingCategoryChanges
          .filter(
            (c): c is Extract<CategoryChange, { type: 'delete' }> =>
              c.type === 'delete' && c.productId === productId
          )
          .map((c) => c.categoryProductId)
      )
      const pendingUpdates = new Map(
        pendingCategoryChanges
          .filter(
            (c): c is Extract<CategoryChange, { type: 'update' }> =>
              c.type === 'update' && c.productId === productId
          )
          .map((c) => [c.categoryProductId, c.newCategoryId])
      )
      const pendingAdds = pendingCategoryChanges.filter(
        (c): c is Extract<CategoryChange, { type: 'add' }> =>
          c.type === 'add' && c.productId === productId
      )

      let result = serverCategories
        .filter((cp) => !pendingDeletes.has(cp.id))
        .map((cp) => {
          const newCategoryId = pendingUpdates.get(cp.id)
          if (newCategoryId != null) {
            const cat = allCategories?.data?.find((c) => c.id === newCategoryId)
            return {
              id: cp.id,
              category_id: newCategoryId,
              category: { title: cat?.title ?? null },
            }
          }
          return cp
        }) as CategoryProductItem[]

      for (const add of pendingAdds) {
        const cat = allCategories?.data?.find((c) => c.id === add.categoryId)
        result.push({
          id: makePendingAddId(productId, add.categoryId),
          category_id: add.categoryId,
          category: { title: cat?.title ?? null },
        })
      }
      return result
    },
    [pendingCategoryChanges, allCategories?.data]
  )

  const [isApplyingCategoryChanges, setIsApplyingCategoryChanges] =
    useState(false)

  const applyCategoryChanges = useCallback(async () => {
    setIsApplyingCategoryChanges(true)
    try {
      const deletes = pendingCategoryChanges.filter(
        (c): c is Extract<CategoryChange, { type: 'delete' }> =>
          c.type === 'delete'
      )
      const updates = pendingCategoryChanges.filter(
        (c): c is Extract<CategoryChange, { type: 'update' }> =>
          c.type === 'update'
      )
      const adds = pendingCategoryChanges.filter(
        (c): c is Extract<CategoryChange, { type: 'add' }> => c.type === 'add'
      )

      await Promise.all(
        deletes
          .filter((c) => !isPendingAddId(c.categoryProductId))
          .map((c) => deleteCategoryProduct.mutateAsync(c.categoryProductId))
      )
      await Promise.all(
        updates.map((c) =>
          updateCategoryProduct.mutateAsync({
            id: c.categoryProductId,
            category_id: c.newCategoryId,
            product_id: c.productId,
          })
        )
      )
      await Promise.all(
        adds.map((c) =>
          createCategoryProduct.mutateAsync({
            category_id: c.categoryId,
            product_id: c.productId,
          })
        )
      )
      setPendingCategoryChanges([])
    } finally {
      setIsApplyingCategoryChanges(false)
    }
  }, [
    pendingCategoryChanges,
    deleteCategoryProduct,
    updateCategoryProduct,
    createCategoryProduct,
    setPendingCategoryChanges,
  ])

  const handleDelete = async () => {
    if (productToDelete) {
      await deleteProduct.mutateAsync(Number(productToDelete))
      setIsDeleteModalOpen(false)
      setProductToDelete(null)
    }
  }

  const DeleteDialog = ({ row }) => (
    <Dialog
      open={isDeleteModalOpen && productToDelete === row?.original?.id}
      onOpenChange={setIsDeleteModalOpen}
    >
      <DialogContent className="w-[90vw]">
        <DialogHeader>
          <DialogTitle>
            {t('Confirm Deletion', 'Bekräfta borttagning')}
          </DialogTitle>

          <DialogDescription>
            {t(
              'Are you sure you want to delete',
              'Är du säker på att du vill ta bort'
            )}{' '}
            "{row?.original?.item_name}"?{' '}
            {t(
              'This action cannot be undone.',
              'Denna åtgärd kan inte ångras.'
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button onClick={() => setProductToDelete(null)}>
            {t('Cancel', 'Avbryt')}
          </Button>

          <Button
            onClick={handleDelete}
            className="bg-red-500 hover:bg-red-600"
          >
            {t('Delete', 'Ta bort')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  const columns = [
    {
      id: 'select',
      header: ({ table }: { table: TanstackTable<TableProduct> }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          setChecked={(checked) => table.toggleAllPageRowsSelected(!!checked)}
          aria-label={t('Select all', 'Markera alla')}
          label=""
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          setChecked={(checked) => row.toggleSelected(!!checked)}
          aria-label={t('Select row', 'Markera rad')}
          label=""
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'source',
      header: ({ column }) => (
        <SortableHeader column={column}>{t('Source', 'Källa')}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-[80px] py-2">
          <span className="text-sm text-gray-700 capitalize">
            {row.original.source}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'item_name',
      header: ({ column }) => (
        <SortableHeader column={column}>{t('Title', 'Titel')}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-[200px] max-w-[300px] py-2">
          <div className="flex items-start gap-2 mb-1">
            <span className="text-xs font-medium text-gray-500">
              {row.original.article_number || row.original.item_code}
            </span>
            {row.original.klayder_recommends && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge className="bg-green-500 text-white text-xs rounded-full p-0.5 hover:bg-green-600 transition-colors shrink-0">
                      <ShieldCheckIcon className="h-3 w-3" />
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('Klayder Recommended', 'Klayder Rekommenderad')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {!row.original.item_name_translations && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant={'destructive'}>
                      <AlertTriangle className="h-3 w-3 text-white" />
                    </Badge>
                  </TooltipTrigger>

                  <TooltipContent>
                    <p>{t('Missing translations', 'Saknar översättningar')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <button
            onClick={() => {
              navigate(
                `${productDetailBasePath ?? paths.adminProducts}/${row.original.id}`
              )
            }}
            className="text-left hover:text-blue-600 hover:underline transition-colors cursor-pointer"
            title={t('Click to edit', 'Klicka för att redigera')}
          >
            <span className="text-sm text-gray-900 line-clamp-2 block">
              {row.original.item_name}
            </span>
          </button>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        return rowA.original.item_name.localeCompare(rowB.original.item_name)
      },
      accessorFn: (row) => row.item_name,
    },
    {
      accessorKey: 'image_url',
      header: ({ column }) => (
        <SortableHeader column={column}>{t('Image', 'Bild')}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-[80px] py-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="relative w-14 h-14 rounded-md overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer bg-gray-50 flex items-center justify-center">
                  <Image
                    src={row.original.image_url}
                    alt={row.original.item_name}
                    width={56}
                    height={56}
                    className="object-cover w-full h-full"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent className="z-50 p-2">
                <Image
                  src={row.original.image_url}
                  alt={row.original.item_name}
                  width={300}
                  className="rounded-lg border shadow-lg"
                />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
    {
      accessorKey: 'final_price',
      header: ({ column }) => (
        <SortableHeader column={column}>Pris</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-[90px] text-left py-2">
          <span className="text-sm font-semibold text-gray-900">
            {row.original.price} {currency}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'categories',
      header: ({ column }) => (
        <SortableHeader column={column}>Categories</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-[200px] py-2">
          <CollapsibleCategories
            categories={getEffectiveCategories(
              row.original.id,
              row.original.category_product ?? []
            )}
            allCategories={allCategories}
            productId={row.original.id}
            onDelete={(id) =>
              addChange({
                type: 'delete',
                categoryProductId: id,
                productId: row.original.id,
              })
            }
            onUpdate={(categoryProductId, newCategoryId) =>
              addChange({
                type: 'update',
                categoryProductId,
                newCategoryId,
                productId: row.original.id,
              })
            }
            onAdd={(categoryId) =>
              addChange({ type: 'add', productId: row.original.id, categoryId })
            }
          />
        </div>
      ),
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t('Created At', 'Skapad')}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-[100px] py-2">
          <span className="text-sm text-gray-600">
            {dayjs(row.original.created_at).fromNow()}
          </span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        return rowA.original.created_at.localeCompare(rowB.original.created_at)
      },
      accessorFn: (row) => row.created_at,
    },
  ]

  return {
    columns,
    hasPendingCategoryChanges: pendingCategoryChanges.length > 0,
    applyCategoryChanges,
    isApplyingCategoryChanges,
  }
}
